import { useRef, useState } from 'react';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { useLanguage } from '../../shell/language-context';
import { ReelTile } from '../reels/reel-rail';
import { ReelViewer, type ReelCard } from '../reels/reel-viewer';
import { MarqueeControls, centreOffset } from './marquee-controls';

/** Below this many reels the rail stays still: a loop of one or two tiles reads as broken. */
export const MARQUEE_MIN_REELS = 4;
/** Seconds per tile, so the drift keeps one speed whatever the reel count (~50 s a lap for 10). */
const SECONDS_PER_TILE = 5;
/** The track's gap (home.css .hm-rail__track) — centreOffset needs it to land on a tile. */
const GAP = 16;

// The homepage "Reels." rail as a slow left-to-right marquee (the community strip's
// direction), the same machinery as the offers rail: the reels repeated until the loop
// never runs dry, then doubled for the seamless -50% keyframe played in reverse. It stops
// on hover, while a tile has keyboard focus, off-screen (use-home-motion) and under reduced
// motion (motion.css); play/pause and one dot per reel sit under it — a dot stops the loop
// and centres that reel. Only the first copy of each reel is in the Tab order and the
// accessibility tree; any tile, copy or not, opens the viewer on its reel.
/** Tab landed here (not a click): only then does focus stop the loop and centre the tile. */
function keyboardFocus(el: HTMLElement): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return false;
  }
}

export function ReelsMarquee({ reels }: { reels: ReelCard[] }) {
  const { t, lang } = useLanguage();
  const railRef = useRef<HTMLDivElement>(null);
  const lastTile = useRef<HTMLElement | null>(null);
  const [dot, setDot] = useState<number | null>(null);
  const [shift, setShift] = useState(0);
  const [open, setOpen] = useState<number | null>(null);

  const half = Array.from({ length: Math.ceil(8 / reels.length) }, () => reels).flat();
  const loop = [...half, ...half];

  const select = (i: number) => {
    const rail = railRef.current;
    const first = rail?.querySelector<HTMLElement>('.rr__tile');
    setDot(i);
    setShift(centreOffset(i, rail?.clientWidth ?? window.innerWidth, first?.offsetWidth ?? 160, GAP));
  };

  return (
    <>
      <div className="hm-rail hm-rail--reels" ref={railRef}>
        <div
          className="hm-rail__track hm-rail__track--reels"
          data-motion="true"
          data-marquee="true"
          style={
            dot !== null
              ? { animation: 'none', transform: `translateX(${shift}px)` }
              : { animationDuration: `${half.length * SECONDS_PER_TILE}s` }
          }
        >
          {loop.map((r, i) => {
            const at = i % reels.length;
            return (
              <ReelTile
                key={`${r.id}-${i}`}
                reel={r}
                showBusiness
                copy={i >= reels.length}
                onFocus={i < reels.length ? (el) => keyboardFocus(el) && select(at) : undefined}
                onOpen={(el) => {
                  lastTile.current = el;
                  setOpen(at);
                }}
              />
            );
          })}
        </div>
      </div>
      <MarqueeControls
        className="hm-mq--reels"
        count={reels.length}
        selected={dot}
        onSelect={select}
        onToggle={() => (dot !== null ? setDot(null) : select(0))}
        playLabel={t('dc50.offers.autoplay')}
        dotLabel={(i) => t('reels.reel_n', { n: formatNumber(lang, i + 1) })}
      />
      {open !== null ? <ReelViewer reels={reels} start={open} onClose={() => setOpen(null)} returnFocus={lastTile.current} /> : null}
    </>
  );
}
